import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ClipboardEdit } from "lucide-react";

const TERMS = ["1", "2", "3"];
const fmt = (v) => Number(v).toFixed(1).replace(".", ",");

function ClassCard({ c }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <Card className="border-slate-200 p-4" data-testid={`noa-class-${c.class_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-slate-900">Turma {c.class_name}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {TERMS.map((t) => (
              <Badge key={t} variant="outline" className={c.by_term[t] ? "border-red-200 bg-red-50 text-red-700" : "text-slate-400"} data-testid={`noa-class-${c.class_id}-term-${t}`}>
                {t}º tri: {c.by_term[t] || 0}
              </Badge>
            ))}
          </div>
        </div>
        <span className="font-display text-3xl font-black text-red-600" data-testid={`noa-class-total-${c.class_id}`}>{c.total}</span>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} data-testid={`noa-class-toggle-${c.class_id}`}>
          {open ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}Alunos
        </Button>
      </div>
      {open && (
        <ul className="mt-3 space-y-1" data-testid={`noa-class-list-${c.class_id}`}>
          {c.items.map((it) => (
            <li key={`${it.student_id}-${it.subject_id}-${it.term_order}`} className="flex flex-wrap items-center gap-x-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span className="font-medium text-slate-800">{it.full_name}</span>
              <span className="text-slate-500">{it.subject_name} • {it.term_order}º tri</span>
              <span className="ml-auto font-semibold text-red-600">{fmt(it.base)}</span>
            </li>
          ))}
          <li className="pt-1">
            <Button size="sm" onClick={() => navigate(`/turmas/${c.class_id}/notas-faltas`)} className="bg-orange-500 hover:bg-orange-600" data-testid={`noa-class-open-editor-${c.class_id}`}>
              <ClipboardEdit className="mr-1.5 h-4 w-4" />Abrir notas da turma
            </Button>
          </li>
        </ul>
      )}
    </Card>
  );
}

export default function PendenciasNoa() {
  const { scopeParams } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/noa/pending", { params: scopeParams }).then((r) => setData(r.data)).catch(() => setData({ total: 0, classes: [] }));
  }, [scopeParams]);
  const totals = TERMS.map((t) => (data?.classes || []).reduce((s, c) => s + (c.by_term[t] || 0), 0));
  return (
    <div data-testid="pendencias-noa-page">
      <PageHeader title="Pendências de NOA" subtitle="Notas do trimestre abaixo de 5,0 sem NOA lançada, em todas as turmas da escola" />
      {!data ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p> : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="border-red-200 bg-red-50 p-4 text-red-700" data-testid="noa-total">
              <AlertTriangle className="h-5 w-5" /><div className="mt-1 font-display text-3xl font-black">{data.total}</div><div className="text-xs font-semibold">Total pendente</div>
            </Card>
            {TERMS.map((t, i) => (
              <Card key={t} className="border-slate-200 p-4" data-testid={`noa-total-term-${t}`}>
                <div className="font-display text-3xl font-black text-slate-900">{totals[i]}</div><div className="text-xs font-semibold text-slate-500">{t}º trimestre</div>
              </Card>
            ))}
          </div>
          {!data.classes.length
            ? <EmptyState icon={CheckCircle2} title="Nenhuma NOA pendente" description="Todas as notas abaixo de 5,0 já têm NOA lançada." />
            : <div className="space-y-3">{data.classes.map((c) => <ClassCard key={c.class_id} c={c} />)}</div>}
        </>
      )}
    </div>
  );
}
